import { useMemo } from 'react'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { reportLog } from 'services/userMonitoring'

function useOfferRating(
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary | App.VillaOffer | App.VillaOfferSummary,
  offerListMeta: App.OfferListMetaData | undefined,
  isMetaSearchPage?: boolean,
): App.OfferRating | undefined {
  const type = offer.type === 'bedbank_hotel' ? 'bedbank' : 'luxuryescapes'
  const property = offer.property

  return useMemo(() => {
    if (!isRatingConsistent(offerListMeta?.rating, property.rating)) {
      reportLog('Offer rating inconsistency detected', {
        offerId: offer.id,
        propertyId: property.id,
        type,
        searchRating: offerListMeta?.rating,
        publicOfferRating: property.rating,
      }, 'warn')
    }

    if (offerListMeta?.rating) {
      return {
        score: offerListMeta.rating.score,
        reviewsTotal: offerListMeta.rating.totalReviews,
      } as App.OfferRating
    }

    if (isMetaSearchPage) {
      return {
        score: offer.property.rating?.score,
        reviewsTotal: offer.property.rating?.reviewsTotal,
      } as App.OfferRating
    }

    // Fallback to offer.property.rating when offerListMeta is not available
    // This ensures rating is displayed even when search metadata is incomplete
    if (offer.property.rating?.score !== undefined) {
      return {
        score: offer.property.rating.score,
        reviewsTotal: offer.property.rating.reviewsTotal,
      } as App.OfferRating
    }
  }, [offerListMeta?.rating, property, type, offer.id, isMetaSearchPage, offer.property.rating])
}

function isRatingConsistent(
  offerSearchRating: App.OfferListMetaData['rating'] | undefined,
  propertyRating: App.OfferRating | undefined,
): boolean {
  const isRatingDisplayable = isOfferRatingDisplayable(propertyRating)

  if (!offerSearchRating && !isRatingDisplayable) return true
  if (!offerSearchRating || !isRatingDisplayable) return false

  return (
    offerSearchRating.score === propertyRating.score &&
    offerSearchRating.totalReviews === propertyRating.reviewsTotal
  )
}

export default useOfferRating
